import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import StoryCarousel from "@/components/StoryCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOST = process.env.REACT_APP_BACKEND_URL;

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useSEO({
    title: "Gallery — GG Fitness | Gora Ganguly | Personal Coach in Action",
    description: "Snapshots of coaching sessions, training moments, workshops and events with Gora Ganguly — certified personal trainer in Bolpur, Santiniketan.",
  });

  useEffect(() => {
    axios.get(`${API}/public/gallery`).then((r) => setImages(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(images.map((i) => i.category).filter(Boolean)))];
  const filtered = activeCategory === "All" ? images : images.filter((i) => i.category === activeCategory);

  // Stories: top 3 most-recent featured items (by created_at desc).
  const stories = useMemo(() => {
    const sorted = [...images].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return sorted.slice(0, 3);
  }, [images]);

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const prev = (e) => { e?.stopPropagation(); setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length); };
  const next = (e) => { e?.stopPropagation(); setLightboxIdx((i) => (i + 1) % filtered.length); };

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIdx, filtered.length]);

  return (
    <div data-testid="gallery-page">
      <div className="page-header">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <span className="font-heading text-gg-red text-xs uppercase tracking-[0.4em]">Gallery</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight mt-2">
            Coaching <span className="text-gg-red">In Action</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-3 max-w-2xl">
            Moments from training sessions, workshops, and the journey of helping people transform their lives.
          </p>
        </div>
      </div>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Story Carousel — auto-shows when there are 3+ images */}
          {stories.length >= 3 && <StoryCarousel stories={stories} />}

          {/* Category filters */}
          {categories.length > 1 && (
            <div className="flex gap-2 flex-wrap mb-6" data-testid="gallery-filters">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`gallery-filter-${cat.toLowerCase()}`}
                  className={`text-xs font-heading uppercase tracking-widest px-4 py-2 rounded-lg transition-all duration-300 ${
                    activeCategory === cat
                      ? "bg-gg-red text-white shadow-lg shadow-gg-red/25"
                      : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array(8).fill(0).map((_, i) => <div key={i} className="aspect-square bg-gg-surface/40 rounded-lg animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="gallery-empty">
              <ImageIcon size={48} className="text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-sm">No images in this category yet. Check back soon.</p>
            </div>
          ) : (
            <div className="masonry-grid">
              {filtered.map((img, i) => (
                <motion.button
                  key={img.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.6) }}
                  onClick={() => openLightbox(i)}
                  data-testid={`gallery-item-${i}`}
                  className="masonry-item group block w-full text-left rounded-xl overflow-hidden border border-white/5 bg-gg-surface card-lift relative cursor-pointer"
                >
                  <img
                    src={`${HOST}${img.url}`}
                    alt={img.caption || "GG Fitness gallery"}
                    loading="lazy"
                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {(img.caption || img.category) && (
                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 translate-y-2 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                      {img.category && <span className="text-gg-red text-[9px] font-heading uppercase tracking-[0.3em] block mb-1">{img.category}</span>}
                      {img.caption && <p className="text-white font-heading text-sm uppercase tracking-wider leading-snug">{img.caption}</p>}
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && filtered[lightboxIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={closeLightbox}
              data-testid="gallery-lightbox-close"
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-all z-10"
            >
              <X size={24} />
            </button>
            {filtered.length > 1 && (
              <>
                <button
                  onClick={prev}
                  data-testid="gallery-lightbox-prev"
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 sm:p-3 rounded-full hover:bg-white/10 transition-all z-10"
                >
                  <ChevronLeft size={28} />
                </button>
                <button
                  onClick={next}
                  data-testid="gallery-lightbox-next"
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 sm:p-3 rounded-full hover:bg-white/10 transition-all z-10"
                >
                  <ChevronRight size={28} />
                </button>
              </>
            )}
            <motion.div
              key={filtered[lightboxIdx].id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl max-h-[88vh] w-full flex flex-col items-center"
            >
              <img
                src={`${HOST}${filtered[lightboxIdx].url}`}
                alt={filtered[lightboxIdx].caption || "GG Fitness"}
                className="max-w-full max-h-[78vh] w-auto h-auto rounded-xl shadow-2xl"
              />
              {(filtered[lightboxIdx].caption || filtered[lightboxIdx].category) && (
                <div className="mt-3 text-center">
                  {filtered[lightboxIdx].category && <span className="text-gg-red text-[10px] font-heading uppercase tracking-[0.4em] block mb-1">{filtered[lightboxIdx].category}</span>}
                  {filtered[lightboxIdx].caption && <p className="text-white font-heading text-base uppercase tracking-wider">{filtered[lightboxIdx].caption}</p>}
                  <p className="text-zinc-500 text-[10px] mt-2">{lightboxIdx + 1} / {filtered.length}</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
